import '@material/web/icon/icon.js'

import gql from 'graphql-tag'
import { css, html, LitElement } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'

import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { isMobileDevice } from '@operato/utils'
import { DataGrist, FetchOption } from '@operato/data-grist'
import { CommonHeaderStyles } from '@operato/styles'

@customElement('data-key-item-list')
export class DataKeyItemList extends localize(i18next)(LitElement) {
  static styles = [
    CommonHeaderStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;

        background-color: var(--md-sys-color-surface);
      }

      ox-grist {
        flex: 1;
      }
    `
  ]

  @property({ type: Object }) dataKeySet: any
  @state() gristConfig: any
  @query('ox-grist') private grist!: DataGrist

  render() {
    return html`
      <ox-grist
        .mode=${isMobileDevice() ? 'LIST' : 'GRID'}
        .config=${this.gristConfig}
        .fetchHandler=${this.fetchHandler.bind(this)}
      ></ox-grist>

      <div class="footer">
        <div filler></div>
        <button danger @click=${this._deleteDataKeyItems.bind(this)}>
          <md-icon>delete_forever</md-icon>${i18next.t('button.delete')}
        </button>
        <button @click=${this._updateDataKeyItems.bind(this)} done>
          <md-icon>save</md-icon>${i18next.t('button.save')}
        </button>
      </div>
    `
  }

  async firstUpdated() {
    this.gristConfig = {
      list: { fields: ['name', 'description', 'active'] },
      columns: [
        { type: 'gutter', gutterName: 'row-selector', multiple: true },
        {
          type: 'gutter',
          gutterName: 'button',
          icon: 'add',
          handlers: {
            click: 'record-copy'
          }
        },
        { type: 'gutter', gutterName: 'sequence' },
        {
          type: 'gutter',
          gutterName: 'button',
          icon: 'arrow_upward',
          handlers: {
            click: 'move-up'
          }
        },
        {
          type: 'gutter',
          gutterName: 'button',
          icon: 'arrow_downward',
          handlers: {
            click: 'move-down'
          }
        },
        {
          type: 'string',
          name: 'name',
          header: i18next.t('field.name'),
          record: {
            editable: true
          },
          width: 140
        },
        {
          type: 'string',
          name: 'description',
          header: i18next.t('field.description'),
          record: {
            editable: true
          },
          width: 180
        },
        {
          type: 'string',
          name: 'dataKey',
          header: i18next.t('field.data-key'),
          record: {
            editable: true
          },
          width: 180
        },
        {
          type: 'string',
          name: 'tKey',
          header: i18next.t('field.t-key'),
          record: {
            editable: true
          },
          width: 180
        }
      ],
      rows: {
        selectable: {
          multiple: true
        }
      },
      pagination: {
        infinite: true
      },
      sorters: []
    }
  }

  async fetchHandler({ filters, page, limit, sortings = [] }: FetchOption) {
    const dataKeyItems = this.dataKeySet.dataKeyItems || []

    return {
      total: dataKeyItems.length,
      records: dataKeyItems
    }
  }

  async _updateDataKeyItems() {
    this.grist.commit()

    const response = await client.mutate({
      mutation: gql`
        mutation ($id: String!, $patch: DataKeySetPatch!) {
          updateDataKeySet(id: $id, patch: $patch) {
            name
          }
        }
      `,
      variables: {
        id: this.dataKeySet.id,
        patch: {
          dataKeyItems: this.grist.data.records,
          cuFlag: 'M'
        }
      }
    })

    if (!response.errors) {
      await document.dispatchEvent(
        new CustomEvent('notify', {
          detail: {
            message: i18next.t('text.info_x_successfully', {
              x: i18next.t('button.save')
            })
          }
        })
      )
    }
  }

  async _deleteDataKeyItems() {
    if (!confirm(i18next.t('text.sure_to_x', { x: i18next.t('text.delete') }))) {
      return
    }

    this.grist.deleteSelectedRecords(false)
  }
}
